// resources/js/Pages/MediaPage.jsx
import React, { useState, useEffect } from 'react';
import AuthenticatedLayout from '../Layouts/AuthenticatedLayout';
import { Button } from '../shadcn/ui/button';
import { TrashIcon } from '@heroicons/react/24/outline';
import CustomFileInputDialog from '../Components/CustomFileInputDialog';
import { useToast } from '../shadcn/ui/use-toast';
import DeleteConfirmationDialog from '../Components/DeleteConfirmationDialog';
import { Skeleton } from '../shadcn/ui/skeleton';
import axios from '../axios';
import config from "../config";
import MediaViewDialog from '../Components/Dialog/MediaViewDialog';
import { useAuth } from "../context/AuthContext";

const MediaPage = () => {
    const { user } = useAuth(); // Fetch authenticated user from context

    useEffect(() => {
        document.title = 'Βιβλιοθήκη Πολυμέσων - Coffee Roast City';
    }, []);

    const [mediaFiles, setMediaFiles] = useState([]);
    const [isLoadingMedia, setIsLoadingMedia] = useState(true);
    const [error, setError] = useState(null);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [mediaToDelete, setMediaToDelete] = useState(null);
    const [loadingFiles, setLoadingFiles] = useState([]);

    const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
    const [selectedMedia, setSelectedMedia] = useState(null);

    const handleViewMedia = (mediaItem) => {
        console.log(mediaItem);
        setSelectedMedia(mediaItem);
        setIsViewDialogOpen(true);
    };

    const handleCloseViewDialog = () => {
        setIsViewDialogOpen(false);
        setSelectedMedia(null);
    };

    const { toast } = useToast();

    useEffect(() => {
        fetchMedia();
        return () => {
            setMediaFiles([]);
            setLoadingFiles([]);
        };
    }, []);

    const fetchMedia = async () => {
        setIsLoadingMedia(true);
        try {
            const response = await axios.get('/api/media');
            const mediaArray = response.data;
            setLoadingFiles(Array(mediaArray.length).fill(true));

            for (let i = 0; i < mediaArray.length; i++) {
                setMediaFiles(prevFiles => [...prevFiles, mediaArray[i]]);
                setLoadingFiles(prevLoading => {
                    const newLoading = [...prevLoading];
                    newLoading[i] = false;
                    return newLoading;
                });
                await new Promise(resolve => setTimeout(resolve, 200));
            }
            // toast({
            //     title: 'Success',
            //     description: 'Media files loaded successfully',
            //     variant: 'success',
            // });
        } catch (err) {
            setError('Failed to load media.');
            if (err.response && err.response.status === 400) {
                toast({
                    title: 'Error',
                    description: err.response.data.message,
                    variant: 'error',
                });
            } else {
                toast({
                    title: 'Error',
                    description: 'An error occurred while loading the media files',
                    variant: 'error',
                });
            }
            console.error('Error fetching media:', err);
        } finally {
            setIsLoadingMedia(false);
        }
    };

    const handleOpenDeleteDialog = (media) => {
        setMediaToDelete(media);
        setIsDeleteDialogOpen(true);
    };

    const handleCloseDeleteDialog = () => {
        setIsDeleteDialogOpen(false);
        setMediaToDelete(null);
    };

    const handleDelete = async () => {
        if (!mediaToDelete) return;

        try {
            await axios.delete(`/api/media/${mediaToDelete.id}`);
            setMediaFiles(prevFiles => prevFiles.filter(file => file.id !== mediaToDelete.id));
            toast({
                title: 'Success',
                description: 'Media file deleted successfully',
                variant: 'success',
            });
        } catch (error) {
            console.error('Failed to delete media file:', error);
            toast({
                title: 'Error',
                description: 'An error occurred while deleting the media file',
                variant: 'error',
            });
        } finally {
            handleCloseDeleteDialog();
        }
    };

    const handleUploadSuccess = (newMedia) => {
        setMediaFiles(prevFiles => [...prevFiles, ...newMedia]);
        toast({
            title: 'Files uploaded successfully!',
            description: '',
            variant: 'success',
            duration: 3000,
        });
    };

    const handleSaveMedia = (updatedMediaItem) => {
        setMediaFiles((prevMediaFiles) =>
            prevMediaFiles.map((media) =>
                media.id === updatedMediaItem.id ? updatedMediaItem : media
            )
        );
    };

    return (
        <AuthenticatedLayout user={user}>
            <div className="container mx-auto px-4 py-6">
                <h1 className="text-2xl font-bold mt-4">Βιβλιοθήκη Πολυμέσων</h1>
                <div className="media-page">
                    {/* Upload Section */}
                    <div className="media-upload-section mb-10 mr-4 flex justify-end">
                        <CustomFileInputDialog onUploadSuccess={handleUploadSuccess} />
                    </div>

                    {/* Media Files Section */}
                    {isLoadingMedia ? (
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 mx-10">
                            {[...Array(6)].map((_, index) => (
                                <div key={index} className="relative border border-gray-300 rounded-md overflow-hidden">
                                    <Skeleton className="w-full aspect-square object-cover" />
                                </div>
                            ))}
                        </div>
                    ) : error ? (
                        <p>{error}</p>
                    ) : (
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 mx-10">
                            {mediaFiles.map((item, index) => (
                                <div
                                    key={item.id}
                                    className="relative border border-gray-300 rounded-md overflow-hidden shadow-sm hover:shadow-md transition-shadow"
                                >
                                    {loadingFiles[index] ? (
                                        <Skeleton className="w-full aspect-square" />
                                    ) : (
                                        <>
                                            <img
                                                src={config.apiUrl + item.file_path}
                                                alt="media"
                                                className="w-full aspect-square object-cover cursor-pointer"
                                                onClick={() => handleViewMedia(item)}
                                            />
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                className="absolute top-2 right-2 bg-white p-1 shadow-md"
                                                onClick={() => handleOpenDeleteDialog(item)}
                                            >
                                                <TrashIcon className="h-6 w-6 text-red-500" />
                                            </Button>
                                        </>
                                    )}
                                </div>
                            ))}
                        </div>

                    )}

                    {/* Delete Confirmation Dialog */}
                    <DeleteConfirmationDialog
                        isOpen={isDeleteDialogOpen}
                        onClose={handleCloseDeleteDialog}
                        onDelete={handleDelete}
                        confirmationTitle={'Delete media'}
                        confirmationMessage={`Are you sure you want to delete the media file with ID "${mediaToDelete?.id}"?`}
                    />

                    {/* Media View Dialog */}
                    <MediaViewDialog isOpen={isViewDialogOpen} onClose={handleCloseViewDialog} mediaItem={selectedMedia} onSave={handleSaveMedia} />
                </div>
            </div>
        </AuthenticatedLayout>

    );
};

export default MediaPage;
